import { useEffect, useState } from 'react'
import { captureSentryError } from 'src/utils/sentry'

/**
 * Chromium and Edge back a composited layer with a single GPU texture and drop the layer
 * when it exceeds the driver's max texture size, which renders large scans as a blank,
 * flickering box. The file viewer composites its image (pan/zoom plus rotate), so cap the
 * bitmap handed to it well below any realistic texture limit.
 */
export const MAX_PREVIEW_IMAGE_DIMENSION = 2400

const DOWNSCALED_MIME_TYPE = 'image/jpeg'
const DOWNSCALED_QUALITY = 0.92

// Vector and animated formats do not survive a canvas round-trip, so they are served as-is.
const NON_DOWNSCALABLE_EXTENSIONS = ['svg', 'gif']

interface UseDownscaledImageOptions {
  enabled: boolean
  fileExtension?: string
}

interface UseDownscaledImageResult {
  imageSrc: string
  isPreparing: boolean
}

const readImageSize = (objectUrl: string): Promise<{ width: number; height: number }> => {
  return new Promise((resolve, reject) => {
    const probe = new Image()

    probe.onload = () => {
      resolve({ width: probe.naturalWidth, height: probe.naturalHeight })
    }
    probe.onerror = () => {
      reject(new Error('Could not read image dimensions'))
    }

    probe.src = objectUrl
  })
}

const downscaleBlob = async (blob: Blob, width: number, height: number): Promise<Blob> => {
  const scale = MAX_PREVIEW_IMAGE_DIMENSION / Math.max(width, height)
  const targetWidth = Math.max(1, Math.round(width * scale))
  const targetHeight = Math.max(1, Math.round(height * scale))

  // Chromium scales during decode when the resize options are supplied up front, which
  // keeps the full-resolution bitmap from ever being materialised.
  //
  // EXIF orientation is left to the default. Phone photos carry a rotation tag that an `<img>`
  // applies for free, and this was checked against orientations 1/3/6/8: the canvas output is
  // pixel-equivalent to what the browser paints, because the target size below comes from the
  // probe image's already-oriented dimensions. Passing `imageOrientation` explicitly is not an
  // option anyway — this workspace pins TypeScript 4.9, whose lib.dom.d.ts predates
  // `'from-image'`. Revisit if a non-Chromium engine ever has to render these previews.
  const bitmap = await createImageBitmap(blob, {
    resizeWidth: targetWidth,
    resizeHeight: targetHeight,
    resizeQuality: 'high',
  })

  try {
    const canvas = document.createElement('canvas')
    canvas.width = targetWidth
    canvas.height = targetHeight

    const context = canvas.getContext('2d')

    if (!context) {
      throw new Error('Could not acquire a 2d canvas context')
    }

    context.drawImage(bitmap, 0, 0, targetWidth, targetHeight)

    return await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (result) => {
          if (result) {
            resolve(result)

            return
          }

          reject(new Error('Could not encode the downscaled image'))
        },
        DOWNSCALED_MIME_TYPE,
        DOWNSCALED_QUALITY,
      )
    })
  } finally {
    bitmap.close()
  }
}

/**
 * Returns a display source for an image that is safe to composite: the original URL when the
 * image is already small enough or cannot be processed, and a downscaled blob URL otherwise.
 */
export const useDownscaledImage = (
  filePath: string,
  { enabled, fileExtension }: UseDownscaledImageOptions,
): UseDownscaledImageResult => {
  const [imageSrc, setImageSrc] = useState('')
  const [isPreparing, setIsPreparing] = useState(false)

  useEffect(() => {
    if (!filePath) {
      setImageSrc('')
      setIsPreparing(false)

      return
    }

    const extension = fileExtension?.toLowerCase().replace(/^\./, '')

    if (!enabled || (extension && NON_DOWNSCALABLE_EXTENSIONS.includes(extension))) {
      setImageSrc(filePath)
      setIsPreparing(false)

      return
    }

    let isCancelled = false
    let sourceObjectUrl: string | null = null
    let downscaledObjectUrl: string | null = null

    setImageSrc('')
    setIsPreparing(true)

    const prepare = async () => {
      try {
        const response = await fetch(filePath)

        if (!response.ok) {
          throw new Error(`Preview request failed with status ${response.status}`)
        }

        const blob = await response.blob()

        // Cleanup may already have run while the request was in flight; creating the object URL
        // now would leave one with nothing left to revoke it.
        if (isCancelled) {
          return
        }

        sourceObjectUrl = URL.createObjectURL(blob)

        const { width, height } = await readImageSize(sourceObjectUrl)

        if (isCancelled) {
          return
        }

        if (Math.max(width, height) <= MAX_PREVIEW_IMAGE_DIMENSION) {
          setImageSrc(sourceObjectUrl)

          return
        }

        const downscaledBlob = await downscaleBlob(blob, width, height)

        // The full-resolution copy is no longer needed once the smaller one exists.
        URL.revokeObjectURL(sourceObjectUrl)
        sourceObjectUrl = null

        if (isCancelled) {
          return
        }

        downscaledObjectUrl = URL.createObjectURL(downscaledBlob)
        setImageSrc(downscaledObjectUrl)
      } catch (error) {
        captureSentryError(error, {
          message: '[hooks.useDownscaledImage] could not prepare a downscaled preview',
          data: { fileExtension },
        })

        if (!isCancelled) {
          setImageSrc(filePath)
        }
      } finally {
        if (!isCancelled) {
          setIsPreparing(false)
        }
      }
    }

    prepare()

    return () => {
      isCancelled = true

      if (sourceObjectUrl) {
        URL.revokeObjectURL(sourceObjectUrl)
      }

      if (downscaledObjectUrl) {
        URL.revokeObjectURL(downscaledObjectUrl)
      }
    }
  }, [filePath, enabled, fileExtension])

  return { imageSrc, isPreparing }
}
